import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, CalendarRange, PieChart, TrendingUp, Landmark, Receipt, BarChart3, Wallet, CreditCard, PiggyBank, FolderKanban, Settings2 } from 'lucide-react';
import { financeApi } from '../../api/finance';
import { PageHeader } from '../../components/PageHeader';

function money(n: number) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * The real navigation hub for every Finance section, replacing the
 * long flat sidebar list per explicit feedback - same icon-tile
 * quick-actions pattern already used on the HR Dashboard. Financial
 * Position now shows genuinely real figures (Total Payables/
 * Receivables, and Bank Balance once configured) - this was left
 * marked unavailable from Phase 1 and never revisited after Phases
 * 2-4 actually made the data real; fixed here.
 */
export function FinanceDashboardPage() {
  const dashboardQuery = useQuery({ queryKey: ['finance-dashboard'], queryFn: () => financeApi.getDashboard() });
  const data = dashboardQuery.data?.data;

  return (
    <div className="page">
      <PageHeader title="Finance Dashboard" />

      {data && (
        <>
          <div className="kpi-grid">
            <KpiCard icon={<CalendarRange size={20} />} color="#eff6ff" iconColor="#1d4ed8" label="Open Fiscal Year" value={data.foundation.openFiscalYear?.yearLabel ?? 'None'} />
            <KpiCard icon={<BookOpen size={20} />} color="#f0fdf4" iconColor="#15803d" label="Active Accounts" value={`${data.foundation.activeAccountsCount} / ${data.foundation.totalAccountsCount}`} />
            <KpiCard icon={<Landmark size={20} />} color="#fffbeb" iconColor="#b45309" label="Active Grants" value={data.foundation.activeGrantsCount} />
            <KpiCard icon={<PieChart size={20} />} color="#f5f3ff" iconColor="#7c3aed" label="Active Cost Centres" value={data.foundation.activeCostCentresCount} />
          </div>

          <section className="card">
            <h2>Financial Position</h2>
            {data.financials.available ? (
              <div className="project-header-grid">
                <StatCard label="Total Payables (Outstanding)" value={data.financials.totalPayables !== null ? money(data.financials.totalPayables) : '-'} />
                <StatCard label="Total Receivables (Outstanding)" value={data.financials.totalReceivables !== null ? money(data.financials.totalReceivables) : '-'} />
                <StatCard label="Bank Balance" value={data.financials.bankBalance !== null ? money(data.financials.bankBalance) : (data.financials.bankBalanceReason ?? '-')} />
                <StatCard label="Budget Utilization" value={data.financials.budgetUtilizationPercent !== null ? `${data.financials.budgetUtilizationPercent}%` : (data.financials.budgetUtilizationReason ?? '-')} />
              </div>
            ) : (
              <p className="muted">{data.financials.reason}</p>
            )}
          </section>
        </>
      )}

      <div className="quick-actions-grid">
        <Link className="quick-action" to="/finance/accounts">
          <span className="quick-action-icon"><BookOpen size={18} /></span>
          <div><strong>Chart of Accounts</strong><div className="muted">Manage the account structure</div></div>
        </Link>
        <Link className="quick-action" to="/finance/fiscal-years">
          <span className="quick-action-icon"><CalendarRange size={18} /></span>
          <div><strong>Fiscal Years</strong><div className="muted">Periods and closing status</div></div>
        </Link>
        <Link className="quick-action" to="/finance/cost-centres">
          <span className="quick-action-icon"><PieChart size={18} /></span>
          <div><strong>Cost Centres</strong><div className="muted">Manage cost centres</div></div>
        </Link>
        <Link className="quick-action" to="/finance/funding-sources">
          <span className="quick-action-icon"><TrendingUp size={18} /></span>
          <div><strong>Funding Sources</strong><div className="muted">Restricted and unrestricted funds</div></div>
        </Link>
        <Link className="quick-action" to="/finance/grants">
          <span className="quick-action-icon"><Landmark size={18} /></span>
          <div><strong>Grants</strong><div className="muted">Track donor grants</div></div>
        </Link>
        <Link className="quick-action" to="/finance/budgets">
          <span className="quick-action-icon"><Landmark size={18} /></span>
          <div><strong>Budgets</strong><div className="muted">Project budgets by account</div></div>
        </Link>
        <Link className="quick-action" to="/finance/journal-entries">
          <span className="quick-action-icon"><Receipt size={18} /></span>
          <div><strong>Transactions</strong><div className="muted">Journal entries and approvals</div></div>
        </Link>
        <Link className="quick-action" to="/finance/general-ledger">
          <span className="quick-action-icon"><BookOpen size={18} /></span>
          <div><strong>General Ledger</strong><div className="muted">Posted transactions by account</div></div>
        </Link>
        <Link className="quick-action" to="/finance/trial-balance">
          <span className="quick-action-icon"><BarChart3 size={18} /></span>
          <div><strong>Trial Balance</strong><div className="muted">Real-time balance check</div></div>
        </Link>
        <Link className="quick-action" to="/finance/expenses">
          <span className="quick-action-icon"><Wallet size={18} /></span>
          <div><strong>Expenses</strong><div className="muted">Staff expense claims</div></div>
        </Link>
        <Link className="quick-action" to="/finance/payables">
          <span className="quick-action-icon"><CreditCard size={18} /></span>
          <div><strong>Payables</strong><div className="muted">Amounts owed, record payments</div></div>
        </Link>
        <Link className="quick-action" to="/finance/receivables">
          <span className="quick-action-icon"><Receipt size={18} /></span>
          <div><strong>Receivables</strong><div className="muted">Amounts owed to Verdantrise</div></div>
        </Link>
        <Link className="quick-action" to="/finance/income">
          <span className="quick-action-icon"><TrendingUp size={18} /></span>
          <div><strong>Income</strong><div className="muted">Money received - donations, interest, other income</div></div>
        </Link>
        <Link className="quick-action" to="/finance/bank-cash">
          <span className="quick-action-icon"><PiggyBank size={18} /></span>
          <div><strong>Bank &amp; Cash</strong><div className="muted">Manage bank and cash accounts</div></div>
        </Link>
        <Link className="quick-action" to="/finance/projects-finance">
          <span className="quick-action-icon"><FolderKanban size={18} /></span>
          <div><strong>Projects Finance</strong><div className="muted">All projects' Budget vs Actual at a glance</div></div>
        </Link>
        <Link className="quick-action" to="/finance/reports">
          <span className="quick-action-icon"><BarChart3 size={18} /></span>
          <div><strong>Reports</strong><div className="muted">Expenditure by project, donor &amp; account</div></div>
        </Link>
        <Link className="quick-action" to="/finance/settings">
          <span className="quick-action-icon"><Settings2 size={18} /></span>
          <div><strong>Finance Settings</strong><div className="muted">Default accounts and currency</div></div>
        </Link>
      </div>
    </div>
  );
}

function KpiCard({ icon, color, iconColor, label, value }: { icon: React.ReactNode; color: string; iconColor: string; label: string; value: string | number }) {
  return (
    <div className="kpi-card">
      <div className="kpi-icon" style={{ background: color, color: iconColor }}>{icon}</div>
      <div>
        <div className="kpi-value">{value}</div>
        <div className="kpi-label">{label}</div>
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="project-header-field">
      <div className="project-header-field-label">{label}</div>
      <div className="project-header-field-value">{value}</div>
    </div>
  );
}
